/**
 * Strips extra fields from parts, keeping only essential properties.
 *
 * Genkit and other systems may add extra metadata fields to parts.
 * This processor ensures clean, minimal data is:
 * - Sent to clients (reduces payload size)
 * - Stored in Firestore (clean data model)
 *
 * Used in pipelines:
 * - model-to-client: Clean up for client consumption
 * - model-to-firestore: Clean up before storage
 */

import type { MessagePart } from '#schemas/message-schemas.js';
import type { ProcessorContext } from '../types.js';
import type { PartResult } from './PartProcessor.js';

import {
  isCustomPart,
  isDataPart,
  isMediaPart,
  isReasoningPart,
  isResourcePart,
  isTextPart,
  isToolRequestPart,
  isToolResponsePart,
  isWidgetPart,
} from '#schemas/message-schemas.js';

import { PartProcessor } from './PartProcessor.js';

export class StripExtraFieldsProcessor extends PartProcessor {
  readonly name = 'StripExtraFields';

  process(part: MessagePart, context: ProcessorContext): PartResult {
    const normalized = this.normalize(part, context);
    return { parts: [normalized] };
  }

  private normalize(part: MessagePart, context: ProcessorContext): MessagePart {
    const thoughtSignature = part.metadata?.thoughtSignature as string | undefined;
    const providerMetadata = part.metadata?.providerMetadata;
    const normalizedProviderMetadata =
      providerMetadata && typeof providerMetadata === 'object'
        ? (providerMetadata as Record<string, unknown>)
        : undefined;
    const withMeta = (part: MessagePart) => {
      const metadata: Record<string, unknown> = {};
      if (thoughtSignature) {
        metadata.thoughtSignature = thoughtSignature;
      }
      if (normalizedProviderMetadata) {
        metadata.providerMetadata = normalizedProviderMetadata;
      }
      if (Object.keys(metadata).length === 0) {
        return part;
      }
      return { ...part, metadata };
    };
    if (isTextPart(part)) return withMeta({ text: part.text });
    if (isMediaPart(part)) return withMeta({ media: part.media });
    if (isResourcePart(part)) return withMeta({ resource: part.resource });
    if (isReasoningPart(part)) return withMeta({ reasoning: part.reasoning });
    if (isCustomPart(part)) return withMeta({ custom: part.custom });
    if (isWidgetPart(part)) return { widget: part.widget };
    if (isToolRequestPart(part)) return withMeta({ toolRequest: part.toolRequest });
    if (isToolResponsePart(part)) return withMeta({ toolResponse: part.toolResponse });
    if (isDataPart(part)) return withMeta({ data: part.data });
    return withMeta(part);
  }
}
